import React from 'react';
import { Box, Typography, Container, Grid, Button, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import { useTheme } from '../../../ThemeContext';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';

const IndustryPageTemplate = ({
  title,
  subtitle,
  desc,
  image,
  themeColor = '#07a4f5',
  bgGradient,
  features = [],
  marqueeItems = [],
}) => {
  const { isDarkMode } = useTheme();

  const textPrimary = isDarkMode ? '#ffffff' : '#0f172a';
  const textSecondary = isDarkMode ? 'rgba(255, 255, 255, 0.65)' : 'rgba(15, 23, 42, 0.65)';
  const cardBg = isDarkMode ? 'rgba(255, 255, 255, 0.03)' : '#ffffff';
  const cardBorder = isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)';

  // Calculate grid layout to guarantee 100% width with ZERO empty gaps
  // 6 or 3 items -> 3 cards per row (md={4})
  // 4 or 2 items -> 2 cards per row (md={6})
  const isThreeCol = features.length % 3 === 0;
  const mdCol = isThreeCol ? 4 : 6;

  // Fallback dynamic telemetry stream items if none passed
  const defaultMarqueeItems = [
    { icon: '🎬', tag: 'PRODUCTION', text: `Live ${title} engine active across 184 global edge clusters`, time: '2m ago', color: themeColor },
    { icon: '⚡', tag: 'LOW LATENCY', text: 'Cinema feed throughput running at 60 FPS ProRes with 14ms ping', time: '4m ago', color: '#f59e0b' },
    { icon: '✨', tag: 'AI OPTIMIZED', text: `Automated dynamic quality scaling calibrated for ${title}`, time: '7m ago', color: '#ec4899' },
    { icon: '🛡️', tag: 'VERIFIED ID', text: 'Encrypted end-to-end studio rights & SAG-AFTRA compliance', time: '11m ago', color: '#10b981' },
    { icon: '🌐', tag: 'GLOBAL OTT', text: 'Cross-border syndication running with zero buffering overhead', time: '15m ago', color: '#06b6d4' },
    { icon: '🎵', tag: 'AUDIO MASTER', text: 'Dolby Atmos spatial multichannel stream verified', time: '19m ago', color: '#8b5cf6' },
  ];

  const slideItems = marqueeItems.length > 0 ? marqueeItems : defaultMarqueeItems;

  return (
    <Box sx={{ pt: { xs: 12, md: 16 }, pb: 10, background: isDarkMode ? '#030712' : '#f8fafc', minHeight: '100vh' }}>
      
      {/* Hero Section */}
      <Container maxWidth="xl" sx={{ mb: 8 }}>
        <Box
          sx={{
            position: 'relative',
            background: isDarkMode ? 'rgba(255,255,255,0.02)' : '#ffffff',
            borderRadius: '32px',
            overflow: 'hidden',
            border: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
            boxShadow: isDarkMode ? '0 24px 60px rgba(0,0,0,0.5)' : '0 20px 60px rgba(15,23,42,0.06)',
            display: 'flex',
            flexDirection: { xs: 'column-reverse', lg: 'row' },
            alignItems: 'center',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: '50%',
              bottom: 0,
              background: bgGradient || `linear-gradient(135deg, ${themeColor}25 0%, rgba(255,255,255,0) 100%)`,
              zIndex: 0,
            },
          }}
        >
          {/* Text Content */}
          <Box sx={{ p: { xs: 4, sm: 6, md: 8 }, flex: 1, position: 'relative', zIndex: 2 }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Chip
                icon={<AutoAwesomeRoundedIcon sx={{ fontSize: '14px !important', color: themeColor }} />}
                label={`Filmhook ${title} Suite`}
                size="small"
                sx={{
                  bgcolor: `${themeColor}15`,
                  color: themeColor,
                  fontWeight: 800,
                  fontSize: '0.72rem',
                  border: `1px solid ${themeColor}35`,
                }}
              />
            </Box>
            <Typography variant="h2" fontWeight={900} sx={{ color: isDarkMode ? '#fff' : '#0f172a', mb: 2, fontSize: { xs: '2.5rem', md: '3.5rem' }, letterSpacing: '-0.02em' }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="h5" sx={{ color: themeColor, fontWeight: 700, mb: 3 }}>
                {subtitle}
              </Typography>
            )}
            <Typography sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '1.1rem', lineHeight: 1.8, mb: 5, maxWidth: '600px' }}>
              {desc}
            </Typography>
            <Button
              variant="contained"
              size="large"
              href="https://play.google.com/store/apps/details?id=com.filmhookapps"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                background: themeColor,
                color: '#fff',
                borderRadius: '30px',
                px: 4,
                py: 1.6,
                fontWeight: 800,
                textTransform: 'none',
                fontSize: '1rem',
                boxShadow: `0 8px 24px ${themeColor}60`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: themeColor,
                  filter: 'brightness(1.15)',
                  transform: 'translateY(-2px)',
                  boxShadow: `0 12px 32px ${themeColor}80`,
                },
              }}
            >
              Get Started with {title}
            </Button>
          </Box>

          {/* Dynamic Animated Image with Zoom In/Out & Floating Elevation */}
          <Box
            sx={{
              flex: 1,
              p: { xs: 2, sm: 4 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 2,
              perspective: '1000px',
            }}
          >
            {/* Ambient Animated Glow Aura behind image */}
            <Box
              component={motion.div}
              animate={{
                scale: [1, 1.18, 1],
                opacity: [0.35, 0.65, 0.35],
              }}
              transition={{
                duration: 4.5,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              sx={{
                position: 'absolute',
                width: '80%',
                height: '80%',
                borderRadius: '50%',
                background: `radial-gradient(circle, ${themeColor}60 0%, ${themeColor}15 50%, transparent 70%)`,
                filter: 'blur(45px)',
                zIndex: 0,
                pointerEvents: 'none',
              }}
            />

            {/* Glowing Scan Horizon Line Effect */}
            <Box
              component={motion.div}
              animate={{
                y: [-120, 120, -120],
                opacity: [0.2, 0.8, 0.2],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              sx={{
                position: 'absolute',
                width: '90%',
                height: '2px',
                background: `linear-gradient(90deg, transparent, ${themeColor}, #ffffff, ${themeColor}, transparent)`,
                boxShadow: `0 0 15px ${themeColor}, 0 0 30px ${themeColor}`,
                zIndex: 3,
                pointerEvents: 'none',
              }}
            />

            {/* Main Interactive Motion Image with continuous Flow & Zoom */}
            <Box
              component={motion.div}
              animate={{
                y: [0, -14, 0],
                scale: [1, 1.05, 1],
                rotateZ: [0, 1, -1, 0],
              }}
              transition={{
                duration: 5.5,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              whileHover={{
                scale: 1.1,
                rotateZ: 2,
                transition: { duration: 0.35 },
              }}
              sx={{
                width: '100%',
                maxWidth: '580px',
                position: 'relative',
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '24px',
                overflow: 'hidden',
                boxShadow: isDarkMode
                  ? `0 25px 60px rgba(0, 0, 0, 0.8), 0 0 45px ${themeColor}40`
                  : `0 20px 50px rgba(15, 23, 42, 0.15), 0 0 35px ${themeColor}30`,
                border: `2px solid ${themeColor}35`,
                cursor: 'pointer',
              }}
            >
              <Box
                component="img"
                src={image}
                alt={title}
                sx={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  objectFit: 'cover',
                  borderRadius: '22px',
                  transition: 'filter 0.4s ease',
                  '&:hover': {
                    filter: 'brightness(1.08) contrast(1.04)',
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Features Grid & Live Cinema Feed */}
      <Container maxWidth="xl">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: themeColor,
                boxShadow: `0 0 10px ${themeColor}`,
                animation: 'pulse 1.4s infinite',
              }}
            />
            <Typography
              sx={{
                fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                fontSize: '0.72rem',
                fontWeight: 800,
                letterSpacing: '0.1em',
                color: isDarkMode ? '#94a3b8' : '#64748b',
                textTransform: 'uppercase',
              }}
            >
              HIGH-PERFORMANCE CINEMA INFRASTRUCTURE
            </Typography>
          </Box>
          <Typography variant="h3" fontWeight={900} sx={{ color: isDarkMode ? '#fff' : '#0f172a', fontSize: { xs: '2rem', md: '2.8rem' }, letterSpacing: '-0.02em' }}>
            Everything you need for <Box component="span" sx={{ color: themeColor }}>{title}</Box>
          </Typography>
        </Box>
        
        {/* Balanced Grid: 100% width with ZERO GAP */}
        <Grid container spacing={3} justifyContent="center" alignItems="stretch">
          {features.map((feat, idx) => (
            <Grid item xs={12} sm={6} md={mdCol} key={idx} sx={{ display: 'flex' }}>
              <Box
                component={motion.div}
                whileHover={{ y: -7, scale: 1.015 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                sx={{
                  background: cardBg,
                  p: { xs: 3, sm: 3.5, md: 4 },
                  borderRadius: '24px',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  border: `1.5px solid ${cardBorder}`,
                  boxShadow: isDarkMode ? '0 8px 30px rgba(0,0,0,0.4)' : '0 8px 25px rgba(15,23,42,0.05)',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'default',
                  transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '18%',
                    right: '18%',
                    height: '3px',
                    background: themeColor,
                    borderRadius: '0 0 4px 4px',
                    opacity: 0.75,
                    transition: 'all 0.35s ease',
                  },
                  '&:hover': {
                    borderColor: themeColor,
                    boxShadow: `0 16px 40px ${themeColor}25`,
                    background: isDarkMode ? 'rgba(255,255,255,0.055)' : '#ffffff',
                    '&::before': {
                      left: '5%',
                      right: '5%',
                      height: '4px',
                      boxShadow: `0 0 14px ${themeColor}`,
                      opacity: 1,
                    },
                  },
                }}
              >
                {/* Card Top: Icon & Badge */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.2 }}>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: '16px',
                      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}cc 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#ffffff',
                      boxShadow: `0 8px 24px ${themeColor}50`,
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.1) rotate(6deg)',
                      },
                    }}
                  >
                    {feat.icon || <CheckCircleRoundedIcon sx={{ fontSize: 26 }} />}
                  </Box>

                  <Chip
                    label={feat.badge || 'Verified'}
                    size="small"
                    sx={{
                      fontWeight: 800,
                      fontSize: '0.68rem',
                      bgcolor: `${themeColor}15`,
                      color: themeColor,
                      border: `1px solid ${themeColor}35`,
                      height: 24,
                      px: 0.5,
                    }}
                  />
                </Box>

                {/* Card Body: Title & Description */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{
                      color: textPrimary,
                      fontSize: '1.15rem',
                      mb: 1,
                      lineHeight: 1.4,
                    }}
                  >
                    {feat.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: textSecondary,
                      fontSize: '0.88rem',
                      lineHeight: 1.65,
                      overflowWrap: 'break-word',
                    }}
                  >
                    {feat.desc}
                  </Typography>
                </Box>

                {/* Card Footer: Micro Telemetry / Stats */}
                <Box
                  sx={{
                    pt: 2,
                    borderTop: isDarkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    {feat.hasEqualizer && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 14 }}>
                        <Box sx={{ width: 2.5, bgcolor: themeColor, borderRadius: 1, animation: 'eqPulse1 0.7s ease-in-out infinite' }} />
                        <Box sx={{ width: 2.5, bgcolor: themeColor, borderRadius: 1, animation: 'eqPulse2 0.55s ease-in-out infinite' }} />
                        <Box sx={{ width: 2.5, bgcolor: themeColor, borderRadius: 1, animation: 'eqPulse3 0.85s ease-in-out infinite' }} />
                      </Box>
                    )}
                    {feat.hasPulse && (
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: '#10b981',
                          boxShadow: '0 0 8px #10b981',
                          animation: 'pulse 1.4s infinite',
                        }}
                      />
                    )}
                    <Typography sx={{ color: themeColor, fontSize: '0.72rem', fontWeight: 800 }}>
                      {feat.metric || 'Ready to Deploy'}
                    </Typography>
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{
                      color: isDarkMode ? '#94a3b8' : '#64748b',
                      fontSize: '0.68rem',
                      fontWeight: 700,
                    }}
                  >
                    {feat.highlight || '100% Guaranteed'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Dynamic Sliding Activity Stream */}
        <Box
          sx={{
            mt: 5,
            p: 2.5,
            borderRadius: '24px',
            background: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : '#ffffff',
            border: `1.5px solid ${cardBorder}`,
            overflow: 'hidden',
            position: 'relative',
            '&::before, &::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              width: { xs: 40, md: 80 },
              zIndex: 2,
              pointerEvents: 'none',
            },
            '&::before': {
              left: 0,
              background: isDarkMode
                ? 'linear-gradient(to right, #030712, transparent)'
                : 'linear-gradient(to right, #ffffff, transparent)',
            },
            '&::after': {
              right: 0,
              background: isDarkMode
                ? 'linear-gradient(to left, #030712, transparent)'
                : 'linear-gradient(to left, #ffffff, transparent)',
            },
          }}
        >
          {/* Stream Sub-header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.8, px: 0.5, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SensorsRoundedIcon sx={{ fontSize: 16, color: themeColor }} />
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: isDarkMode ? '#94a3b8' : '#64748b',
                  fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                }}
              >
                LIVE NETWORK TELEMETRY • {title.toUpperCase()} PIPELINE
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '0.65rem',
                fontWeight: 700,
                color: '#10b981',
                bgcolor: 'rgba(16, 185, 129, 0.1)',
                px: 1.2,
                py: 0.3,
                borderRadius: '999px',
                border: '1px solid rgba(16, 185, 129, 0.25)',
              }}
            >
              ● 184 Global Nodes Online
            </Typography>
          </Box>

          {/* Marquee Track */}
          <Box
            sx={{
              display: 'flex',
              width: 'max-content',
              animation: 'marqueeSlide 30s linear infinite',
              '&:hover': {
                animationPlayState: 'paused',
              },
              '@keyframes marqueeSlide': {
                '0%': { transform: 'translateX(0)' },
                '100%': { transform: 'translateX(-50%)' },
              },
              '@keyframes eqPulse1': {
                '0%, 100%': { height: 4 },
                '50%': { height: 14 },
              },
              '@keyframes eqPulse2': {
                '0%, 100%': { height: 14 },
                '50%': { height: 5 },
              },
              '@keyframes eqPulse3': {
                '0%, 100%': { height: 7 },
                '50%': { height: 16 },
              },
            }}
          >
            {[...slideItems, ...slideItems].map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.2,
                  mx: 1,
                  px: 1.8,
                  py: 0.8,
                  borderRadius: '999px',
                  background: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : '#f1f5f9',
                  border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.06)',
                  transition: 'all 0.25s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    background: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0',
                    borderColor: item.color,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 15px ${item.color}30`,
                  },
                }}
              >
                <Typography sx={{ fontSize: '0.95rem', lineHeight: 1 }}>{item.icon}</Typography>
                <Chip
                  label={item.tag}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.62rem',
                    fontWeight: 900,
                    bgcolor: `${item.color}20`,
                    color: item.color,
                    border: `1px solid ${item.color}40`,
                    letterSpacing: '0.04em',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    color: textPrimary,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.text}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.68rem',
                    fontWeight: 500,
                    color: isDarkMode ? '#94a3b8' : '#64748b',
                    whiteSpace: 'nowrap',
                    pl: 0.5,
                    borderLeft: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                  }}
                >
                  {item.time}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>

    </Box>
  );
};

export default IndustryPageTemplate;
